import * as React from "react";
import { connect } from "react-redux";

import { bindActionCreators } from "redux";

import { Alert, Button, Card, Checkbox, Group, Modal, Select, Stack, Table, Text, TextInput, Title } from "@mantine/core";

import { DateTime } from "luxon";
import {
  getMyImports,
  importUploadFulfilled,
  queueImport,
  triggerImport,
} from "../../data/actions/importActions";
import ImportApi from "../../data/api/importsApi";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { ImportStatus } from "../../types/imports/ImportStatus";
import { ImportType } from "../../types/imports/ImportType";
import { IImport } from "../../types/imports/import";
import { toDateTimeNullable } from "../../util/date+helper";
import { PersonEditorLink } from "../common/Links";
import { CenteredLoader } from "../common/loader";
import { AdminPageLayout } from "../admin/common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport, faUpload, faPlay, faWarning, faEye } from "@fortawesome/free-solid-svg-icons";
import SportingpulseImportForm from "./SportingpulseImportForm";

interface ImportsPageProps {
  actions: {
    getMyImports: Function;
    triggerImport: Function;
    queueImport: (
      file,
      options: { importType: ImportType, associationId: string | null, importOldGames: boolean },
      uploadProgress,
    ) => Promise<any>;
    importUploadFulfilled: (imp: IImport) => void;
  };
  myImports: string[] | null;
  imports: { [id: string]: IImport };
  associations: IAssociation[];
  isAdmin: boolean;
  // Centralized admin association props
  selectedAssociation?: IAssociation | null;
  // dispatch: Function
}

interface ImportsPageState {
  associationId: string | null;

  queue: {
    importType: ImportType | null;
    file?: string;
    isLoading?: boolean;
    error?: {
      message: string;
      data: any;
    };
  };
  editing: {
    isOpen: boolean;
    importId?: string | null;
    type?: ImportType | null;
  };
  showWarnings: {
    importId?: string | null;
  };
  importOldGames: boolean;
}

const getDisplayDate = (date) =>
  toDateTimeNullable(date)?.toLocaleString(DateTime.DATETIME_HUGE);

const ImportRow: React.FunctionComponent<{
  importJob: IImport;
  onEditTap: Function;
  association: IAssociation;
  showWarnings: Function;
}> = ({ importJob, onEditTap, association, showWarnings }) => {
  const getStatusColor = (status: ImportStatus) => {
    switch (status) {
      case ImportStatus.queued: return "yellow";
      case ImportStatus.imported: return "green";
      case ImportStatus.failed: return "red";
      case ImportStatus.expired: return "gray";
      default: return "gray";
    }
  };

  return (
    <Table.Tr>
      <Table.Td>
        {importJob.warnings?.length > 0 ? (
          <Button
            variant="subtle"
            color="yellow"
            size="xs"
            onClick={() => showWarnings(importJob._id)}
            title="View warnings"
          >
            <FontAwesomeIcon icon={faWarning} />
          </Button>
        ) : null}
      </Table.Td>
      <Table.Td>{association?.name || importJob.associationId}</Table.Td>
      <Table.Td>{importJob.type}</Table.Td>
      <Table.Td>{getDisplayDate(importJob.dateQueued)}</Table.Td>
      <Table.Td>{getDisplayDate(importJob.dateCompleted) || "-"}</Table.Td>
      <Table.Td>
        <Text c={getStatusColor(importJob.status)} fw={500}>
          {importJob.status}
        </Text>
      </Table.Td>
      <Table.Td>
        {importJob.type == ImportType.Sportingpulse ? (
          <Button color="blue" variant="outline" size="sm" onClick={() => onEditTap()}>
            <FontAwesomeIcon icon={faEye} style={{ marginRight: "4px" }} />
            View
          </Button>
        ) : null}
      </Table.Td>
    </Table.Tr>
  );
};

class ImportsPage extends React.PureComponent<
  ImportsPageProps,
  ImportsPageState
> {
  constructor(props, content) {
    super(props, content);
    this.state = {
      associationId: props.selectedAssociation?._id || null,
      queue: {
        importType: ImportType.Sportingpulse,
      },
      editing: {
        isOpen: false,
      },
      showWarnings: {},
      importOldGames: false,
    };
    this.onFileSubmit = this.onFileSubmit.bind(this);
    this.onFileChange = this.onFileChange.bind(this);
    this.triggerImport = this.triggerImport.bind(this);
    this.cancelEdit = this.cancelEdit.bind(this);
    this.prepareError = this.prepareError.bind(this);

    this.props.actions.getMyImports();

    document.title = "Stadium Supervisor - Imports";
  }

  getAssociationId() {
    return this.state.associationId || this.props.selectedAssociation?._id || null;
  }

  componentDidUpdate(prevProps: ImportsPageProps) {
    // Update associationId if centralized selection changes
    if (this.props.selectedAssociation?._id !== prevProps.selectedAssociation?._id) {
      this.setState({
        associationId: this.props.selectedAssociation?._id || null
      });
    }
  }

  // show input window, then after file is input:
  // -> Submit
  // -> show modal with loading indicator
  // -> Show match information
  // -> click save button and finish

  onFileSubmit(e) {
    e.preventDefault();

    if (!this.state.queue.file)
      return alert("You must select a file before uploading");
    if (!this.state.queue.importType)
      return alert("You must select an ImportType");

    this.setState((state) => ({
      ...state,
      queue: { ...state.queue, isLoading: true, error: undefined },
    }));

    this.props.actions
      .queueImport(
        this.state.queue.file,
        {
          importType: this.state.queue.importType,
          associationId: this.getAssociationId(),
          importOldGames: this.state.importOldGames
        },
        (progress) => {
          console.log(`File upload progress: ${progress}`);
        },
      )
      .then((imp) => {
        this.setState((state) => ({
          ...state,
          queue: { ...state.queue, isLoading: false },
        }));
        this.props.actions.importUploadFulfilled(imp);
      })
      .catch((er) => {
        let errorMessage = er.message;
        if (er.response?.data?.error) {
          errorMessage = er.response?.data?.error;
        }

        let errorData: any = [];
        const response: any[] = er.response?.data?.data;
        if (response) {
          errorData = response;
        }

        this.setState((state) => ({
          ...state,
          queue: {
            ...state.queue,
            isLoading: false,
            error: {
              message: errorMessage,
              data: errorData,
            },
          },
        }));
      });
  }

  onFileChange(e) {
    const savedTarget = e.currentTarget;
    if (!savedTarget) return;
    this.setState((st) => {
      const queue = { ...st.queue, file: savedTarget.files[0] };
      return { ...st, queue };
    });
  }

  triggerImport(e) {
    // this.props.actions.triggerImport(this.props.import.id);
  }

  getColorForStatus(status: ImportStatus) {
    switch (status) {
      case ImportStatus.queued:
        return "gray";
      case ImportStatus.imported:
        return "gray";
      case ImportStatus.failed:
        return "danger";
      case ImportStatus.expired:
        return "gray";
    }
  }


  submitButtonIsValid(): boolean {
    return (
      !!this.state.queue.file &&
      !!this.state.queue.importType &&
      !this.state.queue.isLoading
    );
  }

  editImport(imp: IImport) {
    this.setState((state) => ({
      ...state,
      editing: { isOpen: true, importId: imp._id, type: imp.type },
    }));
  }

  cancelEdit() {
    this.setState((state) => ({ ...state, editing: { isOpen: false } }));
  }

  prepareError(error?: { message: string; data: any }) {
    if (!error) return "";

    const { message, data } = error;

    if (!Array.isArray(data)) {
      return (
        <>
          <p>{message}</p>
          <p>{JSON.stringify(data)}</p>
        </>
      );
    }

    function prepareInner(el) {
      if (el.reason) {
        return `Row ${el.row} - ${el.reason}`;
      }
    }

    return (
      <>
        <p>{message}</p>
        <ul>
          {data.map((el) => (
            <li>{prepareInner(el)}</li>
          ))}
        </ul>
      </>
    );
  }

  triggerPlayHQImport() {
    ImportApi.triggerPlayHQImport(this.getAssociationId() || "")
      .then((_) => {

        location.reload();
      })
      .catch(alert);
  }

  render() {
    const currentAssociationHasPlayHQ = !!this.props.associations.filter(
      (a) => a._id == this.getAssociationId(),
    )[0]?.config?.playHQ?.associationId;

    return (
      <AdminPageLayout
        title="Game Imports"
        description="Import data from external sources"
        icon={faFileImport}
      >
        <Stack gap="lg">
          {/* File Import Section */}
          <Card withBorder>
            <Stack gap="md">
              <Group>
                <FontAwesomeIcon icon={faUpload} />
                <Title order={4}>Queue an Import</Title>
              </Group>
              
              <Alert color="yellow">
                <Text size="sm">
                  This page only supports the importing of games ({ImportType.Sportingpulse}). 
                  Please edit referees through the{" "}
                  <PersonEditorLink col="blue">Referee editor</PersonEditorLink>.
                </Text>
              </Alert>

              <Stack gap="md">
                <Select
                  label="Import Type"
                  disabled
                  value={ImportType.Sportingpulse}
                  data={ImportType.all().map((i) => ({
                    value: i,
                    label: i
                  }))}
                />

                <TextInput 
                  type="file" 
                  name="importer" 
                  label="Select File"
                  description="Choose a file to import"
                  onChange={this.onFileChange} 
                />

                <Checkbox
                  label="Import old games"
                  checked={this.state.importOldGames}
                  onChange={(e) => this.setState({ importOldGames: e.currentTarget.checked })}
                />

                {this.state.queue.isLoading && <CenteredLoader />}
                
                {this.state.queue.error && (
                  <Alert color="red" title="Import Error">
                    {this.prepareError(this.state.queue.error)}
                  </Alert>
                )}

                <Group justify="flex-end">
                  <Button
                    leftSection={<FontAwesomeIcon icon={faUpload} />}
                    onClick={this.onFileSubmit}
                    disabled={!this.submitButtonIsValid()}
                    loading={this.state.queue.isLoading}
                  >
                    Submit Import
                  </Button>
                </Group>
              </Stack>
            </Stack>
          </Card>

          {/* PlayHQ Import Section */}
          {this.props.associations.some((a) => a?.config?.playHQ?.associationId) && (
            <Card withBorder>
              <Stack gap="md">
                <Group>
                  <FontAwesomeIcon icon={faPlay} />
                  <Title order={4}>PlayHQ Import</Title>
                </Group>
                
                {currentAssociationHasPlayHQ && !!this.getAssociationId() ? (
                  <Group>
                    <Button
                      leftSection={<FontAwesomeIcon icon={faPlay} />}
                      onClick={() => this.triggerPlayHQImport()}
                      color="green"
                    >
                      Trigger PlayHQ Import
                    </Button>
                    <Text size="sm" c="dimmed">
                      Import games directly from PlayHQ
                    </Text>
                  </Group>
                ) : (
                  <Alert color="blue">
                    <Text size="sm">This association doesn't have PlayHQ imports enabled</Text>
                  </Alert>
                )}
              </Stack>
            </Card>
          )}

          {/* Import History */}
          {this.props.myImports && (
            <Card withBorder>
              <Stack gap="md">
                <Title order={4}>Import History</Title>
                
                <div style={{ overflowX: 'auto' }}>
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={60}>Warnings</Table.Th>
                        <Table.Th>Association</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Date Queued</Table.Th>
                        <Table.Th>Date Completed</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th w={100}>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {this.props.myImports
                        .map((id) => this.props.imports[id])
                        .filter((imp) => !!imp)
                        .map((imp, idx) => (
                          <ImportRow
                            key={imp.id || imp._id || idx}
                            importJob={imp}
                            onEditTap={() => this.editImport(imp)}
                            association={
                              this.props.associations.filter(
                                (a) => a._id == imp.associationId,
                              )[0]
                            }
                            showWarnings={(importId) =>
                              this.setState({ showWarnings: { importId } })
                            }
                          />
                        ))}
                    </Table.Tbody>
                  </Table>
                </div>
              </Stack>
            </Card>
          )}
        </Stack>

        {/* Modals */}
        <Modal
          opened={Boolean(
            this.state.editing.isOpen && this.state.editing.importId,
          )}
          onClose={this.cancelEdit}
          title="Import Details"
          size="lg"
        >
          <SportingpulseImportForm
            importId={this.state.editing.importId!}
            onCancel={this.cancelEdit}
          />
        </Modal>
        
        <Modal
          opened={!!this.state.showWarnings.importId}
          onClose={() => this.setState({ showWarnings: {} })}
          title="Import Warnings"
        >
          <Stack gap="sm">
            {this.props.imports[this.state.showWarnings.importId!]?.warnings?.map((w, idx) => (
              <Alert key={idx} color="yellow" variant="light">
                <Text size="sm">{w}</Text>
              </Alert>
            ))}
          </Stack>
        </Modal>
      </AdminPageLayout>
    );
  }
}

function mapStateToProps(state: IState, ownProps: ImportsPageProps) {
  return {
    imports: state.imports.map,
    myImports: state.imports.self,
    // Use centralized associations if provided, otherwise fall back to Redux
    associations: ownProps.associations || state.associations.admin.map(
      (a) => state.associations.map[a],
    ),
    // @ts-ignore
    isAdmin: state.persons.map[state.persons.self]?.admin || false,
    // Pass through centralized association props
    selectedAssociation: ownProps.selectedAssociation,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        queueImport,
        triggerImport,
        getMyImports,
        importUploadFulfilled: (imp) => dispatch(importUploadFulfilled(imp)),
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ImportsPage);
